import React, { useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useOmniAgentPick } from '../../context/OmniAgentContext';
import { useShell } from '../Shell/ShellContext';
import { panelUrl } from '../Shell/panelFollow';
import { useChangeReview, ChangeRow, ReviewStatus, ReviewActions, LooseNotes } from './ChangeReview';
import './DiffCard.css';

// The resume changes the assistant proposes in a turn: one card per turn, one
// row per change, each with its own tick (founder, 2026-09-27: "I have no way
// to just approve selected one either all or none"). The ticks, the rows and
// applying them live in ChangeReview.js, shared with the Resume panel.
//
// Beside the Resume panel (the shell, 2026-09-28) the review happens there, on
// the resume itself, so the chat keeps a short card that opens it and then
// says how it ended. Elsewhere (a phone, the overlay chat) the card is the
// review.

function ReviewLink({ review, held }) {
  const { changeOutcomes = {} } = useOmniAgentPick('changeOutcomes');
  if (!review.group.length) {
    const ids = [...held];
    const results = ids.map(id => changeOutcomes[id]).filter(Boolean);
    // Nothing held, or decided somewhere this page did not see (a reloaded
    // conversation): say nothing rather than guess.
    if (!ids.length || results.length < ids.length) return null;
    const applied = results.includes('applied');
    return (
      <div className={`ag-cc-status${applied ? ' is-done' : ''}`} role="status">
        <span className="material-symbols-rounded" aria-hidden="true">{applied ? 'check' : 'close'}</span>
        {applied ? 'Applied to your resume' : 'Discarded'}
      </div>
    );
  }
  const titles = [...new Set(review.units.map(u => u.title))];
  const named = titles.length > 2 ? `${titles.slice(0, 2).join(', ')} and ${titles.length - 2} more` : titles.join(' and ');
  return (
    <div className="ag-cc-linkcard">
      <span className="ag-cc-linkcard-icon" aria-hidden="true"><span className="material-symbols-rounded">edit_document</span></span>
      <span className="ag-cc-linkcard-text">
        <strong>{review.total === 1 ? '1 change to your resume' : `${review.total} changes to your resume`}</strong>
        <span title={titles.join(', ')}>{named}</span>
      </span>
      <Link className="ag-cc-btn is-primary" to={panelUrl({ tab: 'Resume', focus: { section: 'review' } })}>Review</Link>
    </div>
  );
}

const DiffCard = ({ changes, change }) => {
  const { inShell } = useShell();
  const group = useMemo(() => (changes && changes.length ? changes : (change ? [change] : [])), [changes, change]);
  const review = useChangeReview(group);
  // Every proposal this card has shown, so it can say how they ended after
  // they leave the pending list.
  const held = useRef(new Set());
  group.forEach(c => { if (c?.changeId) held.current.add(String(c.changeId)); });

  if (inShell) return <ReviewLink review={review} held={held.current} />;
  if (!group.length || review.phase === 'applying') return <ReviewStatus review={review} />;

  return (
    <>
      <ReviewStatus review={review} />
      <div className="ag-cc" role="group" aria-label="Proposed resume changes">
        <LooseNotes review={review} />

        {review.units.map(unit => {
          const sameOpen = review.isSameOpen(unit.key);
          return (
            <section key={unit.key} className="ag-cc-entry" aria-label={unit.title}>
              <header className="ag-cc-head">
                <div className="ag-cc-title">
                  {unit.section && unit.title !== unit.section && <span className="ag-cc-section">{unit.section}</span>}
                  <span className="ag-cc-name" title={unit.title}>{unit.title}</span>
                </div>
                {unit.action !== 'ADD' && (
                  <Link className="ag-cc-icon" to={panelUrl({ tab: 'Resume', focus: { section: unit.type?.toLowerCase(), entry: unit.entryId } })} aria-label={`Show ${unit.title} on the resume`} title="Show on resume">
                    <span className="material-symbols-rounded" aria-hidden="true">visibility</span>
                  </Link>
                )}
              </header>

              {unit.rows.length === 0 && <p className="ag-cc-empty">No changes here.</p>}

              <ul className="ag-cc-rows">
                {unit.rows.map(row => <ChangeRow key={row.key} review={review} row={row} />)}
              </ul>

              {unit.unchanged.length > 0 && (
                <div className="ag-cc-same">
                  <button type="button" className="ag-cc-more" aria-expanded={sameOpen} onClick={() => review.toggleSame(unit.key)}>
                    <span className="material-symbols-rounded" aria-hidden="true">{sameOpen ? 'expand_less' : 'expand_more'}</span>
                    {sameOpen ? 'Hide unchanged' : `${unit.unchanged.length} unchanged line${unit.unchanged.length > 1 ? 's' : ''}`}
                  </button>
                  {sameOpen && (
                    <ul className="ag-cc-lines">{unit.unchanged.map((t, i) => <li key={i}>{t}</li>)}</ul>
                  )}
                </div>
              )}
            </section>
          );
        })}

        <ReviewActions review={review} />
      </div>
    </>
  );
};

export default DiffCard;
